import React, { useState } from 'react'

import { Box, Flex, Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table'
import { RawTextCell } from '../components/Cell'
import TablePagination from '../components/TablePagination'
import TableFilter from '../components/Filter/TableFilter'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa'

const defaultColumn = {
  cell: RawTextCell,
  size: 200,
  minSize: 50,
}
const ClientSideTable = ({
  columnDef,
  data,
  updateData = () => {},
  disabled = false,
  showFooter = false,
  tableWidth = '',
  handleRowClick = () => {},
  canSelect = false,
  rowSelection = [],
  setRowSelection = () => {},
  hiddenColumns = [],
  grouping = [],
  textAlign = 'center',
  ...rest
}) => {
  const tableInstance = useReactTable({
    data,
    defaultColumn,
    columns: columnDef,
    filterFns: {},
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    meta: {
      updateData: updateData,
      disabled: disabled,
    },
  })
  const [state, setState] = useState(tableInstance.initialState)

  tableInstance.setOptions((prev) => ({
    ...prev,
    state,
    onStateChange: setState,
  }))

  return (
    <>
      <TablePagination table={tableInstance} dataCount={tableInstance.getPageCount()} />

      <Table
        variant="simple"
        border="1px solid rgb(226, 232, 240)"
        borderRadius="md"
        w={tableWidth === '' ? tableInstance.getCenterTotalSize() : tableWidth}
      >
        <Thead>
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  p={2}
                  textAlign={textAlign}
                  w={header.getSize() !== 0 ? header.getSize() : undefined}
                  colSpan={header.colSpan}
                  bg={header.column.columnDef?.backgroundColor}
                >
                  <Flex h="100%" direction="column" justifyContent="space-between">
                    <Flex _hover={{ cursor: 'pointer' }} w="100%" onClick={header.column.getToggleSortingHandler()}>
                      <Box w="100%"> {flexRender(header.column.columnDef.header, header.getContext())}</Box>

                      {/* Sorting */}
                      {{ asc: <FaAngleUp />, desc: <FaAngleDown /> }[`${header.column.getIsSorted()}`] ?? null}
                    </Flex>

                    {/* Filter */}
                    {header.column.getCanFilter() ? (
                      <Box mt="1">
                        <TableFilter column={header.column} updatePagination={() => tableInstance.setPageIndex(0)} />
                      </Box>
                    ) : null}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {tableInstance.getRowModel().rows.map((row, rowIndex) => {
            return (
              <Tr key={rowIndex} onClick={() => handleRowClick(row)}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    p={2}
                    textAlign={textAlign}
                    bg={cell.column.columnDef?.backgroundColor}
                    w={cell.column.getSize() !== 0 ? cell.column.getSize() : undefined}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            )
          })}
        </Tbody>

        {showFooter && (
          <Tfoot>
            {tableInstance.getFooterGroups().map((footerGroup) => (
              <Tr key={footerGroup.id}>
                {footerGroup.headers.map((header) => (
                  <Td
                    key={header.id}
                    colSpan={header.colSpan}
                    textAlign={textAlign}
                    bg={header.column.columnDef?.backgroundColor}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tfoot>
        )}
      </Table>
    </>
  )
}

export default ClientSideTable
